/**
 * @file Album Folder Tags utilities.
 */
import get from 'lodash.get';

import {
  ALBUM_FOLDER_TAG_GENDER,
  ALBUM_FOLDER_GENDER_TAG_LABEL,
  ALBUM_FOLDER_TAG_TYPE,
} from '../constants/album-folder-tags';
import { formatPriceForDisplay } from './format-money';
import { IDENTITY_GENDER } from '../constants/identity';
import { sortArrayByKey } from './sorting';

/**
 * Gets tags by type.
 *
 * @param {ALBUM_FOLDER_TAG_TYPE} type - The type to look for.
 * @param {Array} tags - The tags.
 * @returns {Array} - Tags by type.
 */
const getTagsByType = (type, tags) => {
  if (!tags) {
    return [];
  }

  return tags.filter(tag => tag.type === type);
};

/**
 * Returns a label based on the tag type.
 *
 * @param {Tag} tag Tag to get type label from.
 */
const getTagTypeLabel = tag => {
  switch (tag.type) {
    case ALBUM_FOLDER_TAG_TYPE.GRADE:
      return 'Grade tag';
    case ALBUM_FOLDER_TAG_TYPE.UNIFORM:
      return 'Sport/Activity tag';
    case ALBUM_FOLDER_TAG_TYPE.CUSTOM:
      return 'Custom tag';
    default:
      return '';
  }
};

/**
 * Gets tags by gender.
 *
 * @param {ALBUM_FOLDER_TAG_GENDER} gender - The gender to look for.
 * @param {Array} tags - The tags.
 * @returns {Array} - Tags by gender.
 */
const getTagsByGender = (gender, tags) => {
  if (!tags) {
    return [];
  }

  return tags.filter(tag => tag.gender === gender);
};

/**
 * Returns a Uniform tag's label.
 *
 * @param {AlbumFolderTagDetails} tag - The album folder tag.
 * @returns {String} The tag's label.
 */
const getUniformTagLabel = tag => {
  const label = ALBUM_FOLDER_GENDER_TAG_LABEL[tag.gender];

  if (label) {
    return `${label} - ${tag.name}`;
  }

  return tag.name || '';
};

/**
 * Returns a tag's name along with its price if it exists.
 *
 * @param {AlbumFolderTagDetails} tag The album folder tag.
 * @returns {String} The custom tag's name including its price.
 */
const getTagLabelWithPrice = tag => {
  const tagPrice = get(tag, 'digital_downloads_price.value', '');

  return `${tag.name} ${
    tagPrice ? `- $${formatPriceForDisplay(tagPrice)}` : ''
  }`.trim();
};

/**
 * Sorts uniform tags by label (@see getUniformTagLabel).
 *
 * @param {Array<AlbumFolderTagDetails>} tags - The tags.
 * @returns {Array} - The sorted tags.
 */
const sortUniformTagsByLabel = (tags = []) => {
  if (!tags.length) {
    return tags;
  }

  return [...tags].sort((a, b) => {
    return getUniformTagLabel(a).localeCompare(getUniformTagLabel(b));
  });
};

/**
 * Sorts uniform tags by label and groups them by gender.
 *
 * @param {Array<AlbumFolderTagDetails>} tags - The uniform tags to sort and group.
 * @returns {Object} - An object with 'boys' and 'girls' properties containing sorted tag arrays.
 */
const sortUniformTagsByLabelAndGender = (tags = []) => {
  if (!tags.length) {
    return {};
  }

  const girlsTags = tags
    .filter(tag => tag.gender === IDENTITY_GENDER.FEMALE)
    .sort((a, b) => getUniformTagLabel(a).localeCompare(getUniformTagLabel(b)));
  const boysTags = tags
    .filter(tag => tag.gender === IDENTITY_GENDER.MALE)
    .sort((a, b) => getUniformTagLabel(a).localeCompare(getUniformTagLabel(b)));
  if (boysTags.length > 0 || girlsTags.length > 0) {
    return { boys: boysTags, girls: girlsTags };
  }
  return {};
};

/**
 * Sorts a Uniform tag's levels alphabetically by name.
 *
 * @param {Array<Object>} levels - The uniform tag levels.
 * @returns {Array<Object>} - The sorted levels.
 */
const getSortedUniformTagLevels = (levels = []) => {
  if (!levels.length) {
    return [];
  }

  return sortArrayByKey(levels, 'name');
};

/**
 * Determines if a Uniform album folder tag has levels.
 *
 * @param {Object} tag - The uniform album folder tag.
 * @returns {boolean} - Whether the tag has levels or not.
 */
const albumFolderTagHasLevels = tag => get(tag, 'levels.records.length', 0) > 0;

/**
 * Gets and sorts grade tags the way they should logically appear in the UI,
 * e.g. First Pre-K, then Kindergarten, then 1st Grade, 2nd Grade ... 10th.. Etc.
 *
 * @param {Array} tags - The grade tags.
 * @returns {*}
 */
const getSortedAlbumFolderGradeTags = (tags = []) => {
  if (!tags.length) {
    return [];
  }

  const gradeTags = getTagsByType(ALBUM_FOLDER_TAG_TYPE.GRADE, tags);

  return [...gradeTags].sort((a, b) => {
    // .reverse because we want to force exceptions on top and still
    // defined them in this array in their normal order for developer
    // clarity
    const exceptions = ['Preschool', 'Pre-K', 'Kindergarten'].reverse();
    const indexA = exceptions.indexOf(a.name);
    const indexB = exceptions.indexOf(b.name);

    if (indexA === -1 && indexB === -1) {
      // Not using localeCompare here as most grades start with a "number"
      // and we want `10th` to come after `1st`, so we convert to int first
      const numA = parseInt(a.name, 10);
      const numB = parseInt(b.name, 10);

      if (Number.isNaN(numA) || Number.isNaN(numB)) {
        return a.name.localeCompare(b.name);
      }

      return numA - numB;
    }

    // Force exceptions on top
    return indexB - indexA;
  });
};

/**
 * Filters uniform tags by those which fit the
 * provided gender of an identity.
 *
 * @param {String} gender Gender to filter by.
 * @param {Array<Tags>} tags List of uniform tags.
 */
const getEligibleUniformTagsForIdentity = (gender = null, tags = []) => {
  return getTagsByType(ALBUM_FOLDER_TAG_TYPE.UNIFORM, tags).filter(
    tag =>
      tag.gender === gender ||
      // covers null & undefined
      tag.gender === ALBUM_FOLDER_TAG_GENDER.ALL ||
      !tag.gender ||
      !gender,
  );
};

/**
 * Given a list of identities, prepares an array of
 * identities that is ready for
 * identityAlbumFolderTagsCreateMutation.
 *
 * @param {Array} identities The identities to prepare.
 */
const prepareIdentityTagsForCreate = (identities = []) => {
  return identities.map(identity => {
    return {
      identity_id: identity.id,
      album_folder_tags: identity.tags.map(tag => ({
        album_folder_tag_id: tag.id,
        uniform: tag.identityMetadata
          ? {
              uniform_album_folder_tag_level_id: tag.identityMetadata.levelId,
              uniform_album_folder_tag_level_team_id:
                tag.identityMetadata.teamId,
              jersey_number: tag.identityMetadata.jerseyNumber,
            }
          : undefined,
      })),
    };
  });
};

/**
 * Given an identity, returns its current grade tag.
 *
 * @param {Identity} identity The identity object.
 */
const getIdentityGrade = identity => {
  const identityTags = get(identity, 'album_folder_tags.records', []);
  const identityGradeTags = identityTags.filter(identityTag => {
    return (
      get(identityTag, 'album_folder_tag.type') === ALBUM_FOLDER_TAG_TYPE.GRADE
    );
  });

  return identityGradeTags.length > 0
    ? identityGradeTags[0].album_folder_tag
    : undefined;
};

const getTeamLabel = (team, removeJoinedCount = false) => {
  if (!team) {
    return 'N/A';
  }

  let baseLabel = '';
  if (team.name && !team.coach_name) {
    baseLabel = team.name;
  } else if (!team.name && team.coach_name) {
    baseLabel = `Coach: (${team.coach_name})`;
  } else {
    baseLabel = `${team.name} (${team.coach_name})`;
  }

  if (!removeJoinedCount && team.joined_identities_count) {
    return `${baseLabel} (${team.joined_identities_count} joined)`;
  }

  return baseLabel;
};

/**
 * Filters a list of jerseys by venue.
 *
 * @param {Array<Object>} jerseys List of jerseys to filter.
 * @param {String}  venue Venue to filter by.
 * @returns {Array<Object>} Filtered jerseys by venue.
 */
const getJerseysByVenue = (jerseys = [], venue) =>
  jerseys.filter(jersey => jersey.venue === venue);

const getLevelIdsByTag = uniformTag =>
  (uniformTag?.levels?.records ?? []).map(level => level.id);

export {
  getTagsByGender,
  getTagsByType,
  getUniformTagLabel,
  getEligibleUniformTagsForIdentity,
  prepareIdentityTagsForCreate,
  getIdentityGrade,
  sortUniformTagsByLabel,
  albumFolderTagHasLevels,
  getSortedAlbumFolderGradeTags,
  getTagTypeLabel,
  getTagLabelWithPrice,
  getTeamLabel,
  getJerseysByVenue,
  getLevelIdsByTag,
  sortUniformTagsByLabelAndGender,
  getSortedUniformTagLevels,
};
